import { computed, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import entityPulseApi from '@/api/modules/entityPulseApi';
import type {
  EntityMonitoringPolicyItem,
  EntityMonitoringStatusResponse,
  PulseEntityEventCountsResponse,
} from '@/types/entityPulse';
import { normalizeTaxId } from '@/utils/formatters';

export interface EntityMonitoringView {
  isMonitored: boolean;
  policies: EntityMonitoringPolicyItem[];
  isLoading: boolean;
}

interface UseEntityMonitoringOptions {
  entityType: 'company' | 'person';
  /** Already the id the surfaces key on: company = 8-char CNPJ root, person = CPF. Null until resolved. */
  taxId: Ref<string | null | undefined> | ComputedRef<string | null | undefined>;
  /** Feature-gated: only fetch when the account/user may see Pulse. */
  enabled: Ref<boolean> | ComputedRef<boolean>;
}

// The entity's monitoring status (Postgres), shared by the banner icon and the Pulse tab: one request at a time per
// entity, and the latest answer for both — so an add / remove on the Pulse tab also updates the banner.
const statusByEntity = ref(new Map<string, EntityMonitoringStatusResponse>());
const statusRequests = new Map<string, Promise<EntityMonitoringStatusResponse>>();

const statusKey = (entityType: 'company' | 'person', taxId: string) => `${entityType}:${taxId}`;

/**
 * The entity's monitoring status. Joins a request already in flight; otherwise asks the backend, unless `reuse` and
 * this page already has an answer.
 */
export function fetchEntityMonitoringStatus(
  entityType: 'company' | 'person',
  taxId: string,
  { reuse = false }: { reuse?: boolean } = {},
): Promise<EntityMonitoringStatusResponse> {
  const key = statusKey(entityType, taxId);
  const inFlight = statusRequests.get(key);
  if (inFlight) return inFlight;
  const known = statusByEntity.value.get(key);
  if (reuse && known) return Promise.resolve(known);

  const request = entityPulseApi.getMonitoringStatus({ taxId, entityType })
    .then((response) => {
      statusByEntity.value.set(key, response);
      return response;
    })
    .finally(() => statusRequests.delete(key));
  statusRequests.set(key, request);
  return request;
}

/**
 * Monitoring status for the banner "Monitorado" icon: whether an active monitoring policy currently
 * enrolls the entity (real membership from the config, not events) + the policies enrolling it.
 *
 * Backed by Postgres (fast) and asked again on every page visit, so that adding/removing the entity from a
 * policy shows up on the next visit (and at once when done on the Pulse tab, which shares the answer).
 */
export function useEntityMonitoringStatus(options: UseEntityMonitoringOptions) {
  const isLoading = ref(false);

  const targetTaxId = computed(() => {
    if (!options.enabled.value) return null;
    return normalizeTaxId(options.taxId.value) || null;
  });

  const status = computed(() => {
    const taxId = targetTaxId.value;
    return taxId ? statusByEntity.value.get(statusKey(options.entityType, taxId)) ?? null : null;
  });

  const load = async (taxId: string) => {
    isLoading.value = true;
    try {
      await fetchEntityMonitoringStatus(options.entityType, taxId);
    } catch (error) {
      console.error('Failed to load entity monitoring status:', error);
    } finally {
      if (targetTaxId.value === taxId) isLoading.value = false;
    }
  };

  watch(targetTaxId, (taxId) => {
    if (!taxId) {
      isLoading.value = false;
      return;
    }
    void load(taxId);
  }, { immediate: true });

  return {
    view: computed<EntityMonitoringView>(() => ({
      isMonitored: status.value?.is_monitored ?? false,
      policies: status.value?.policies ?? [],
      isLoading: isLoading.value,
    })),
  };
}

// The entity's event counts per event tab, shared like the status: the page's Pulse tab badge (their sum) loads them
// before the Pulse tab opens, and the Pulse tab's event tabs reuse them.
const eventCountsByEntity = ref(new Map<string, PulseEntityEventCountsResponse>());
const eventCountRequests = new Map<string, Promise<PulseEntityEventCountsResponse>>();

/**
 * The entity's event counts (own / others). Joins a request already in flight; otherwise asks the backend, unless
 * `reuse` and this page already has an answer.
 */
export function fetchEntityEventCounts(
  taxId: string,
  { reuse = false }: { reuse?: boolean } = {},
): Promise<PulseEntityEventCountsResponse> {
  const inFlight = eventCountRequests.get(taxId);
  if (inFlight) return inFlight;
  const known = eventCountsByEntity.value.get(taxId);
  if (reuse && known) return Promise.resolve(known);

  const request = entityPulseApi.getEntityEventCounts(taxId)
    .then((response) => {
      eventCountsByEntity.value.set(taxId, response);
      return response;
    })
    .finally(() => eventCountRequests.delete(taxId));
  eventCountRequests.set(taxId, request);
  return request;
}

/**
 * Count of all the entity's Pulse events — from its own monitoring plus other entities' monitoring — for the count
 * badge on the Pulse tab. Asked again on every page visit; follows a reload on the Pulse tab, which shares it.
 */
export function useEntityEventCount(options: UseEntityMonitoringOptions) {
  const isEventCountLoading = ref(false);

  const targetTaxId = computed(() => {
    if (!options.enabled.value) return null;
    return normalizeTaxId(options.taxId.value) || null;
  });

  const eventCount = computed(() => {
    const counts = targetTaxId.value ? eventCountsByEntity.value.get(targetTaxId.value) : undefined;
    return counts ? counts.own_events + counts.others_events : null;
  });

  const load = async (taxId: string) => {
    isEventCountLoading.value = true;
    try {
      await fetchEntityEventCounts(taxId);
    } catch (error) {
      console.error('Failed to load the entity pulse event count:', error);
    } finally {
      if (targetTaxId.value === taxId) isEventCountLoading.value = false;
    }
  };

  watch(targetTaxId, (taxId) => {
    if (!taxId) {
      isEventCountLoading.value = false;
      return;
    }
    void load(taxId);
  }, { immediate: true });

  return { eventCount, isEventCountLoading };
}
